import {
	ActionIcon,
	Badge,
	Checkbox,
	CopyButton,
	Group,
	Menu,
	Table,
	Text,
	Tooltip,
	UnstyledButton,
	Center,
} from "@mantine/core";
import {
	IconCheck,
	IconClock,
	IconCopy,
	IconDotsVertical,
	IconEdit,
	IconInfinity,
	IconPlayerPause,
	IconPlayerPlay,
	IconPower,
	IconRecycle,
	IconRefresh,
	IconTrash,
	IconX,
	IconChevronUp,
	IconChevronDown,
	IconSelector,
} from "@tabler/icons-react";
import type { License } from "../../store/licenses/licensesSlice";

export type SortDirection = "asc" | "desc" | null;
export type LicenseSortField =
	| "name"
	| "uid"
	| "clientUid"
	| "activationDate"
	| "account"
	| "os"
	| "status"
	| "expirationDate"
	| "daysLeft"
	| "daysSinceHold"
	| null;

interface ThSortableProps {
	children: React.ReactNode;
	sorted: boolean;
	reversed: boolean;
	onSort: () => void;
}

function ThSortable({ children, sorted, reversed, onSort }: ThSortableProps) {
	const Icon = sorted
		? reversed
			? IconChevronUp
			: IconChevronDown
		: IconSelector;
	return (
		<Table.Th>
			<UnstyledButton
				onClick={onSort}
				style={{
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					padding: "4px 0",
					cursor: "pointer",
				}}>
				<Text
					fw={500}
					size="sm">
					{children}
				</Text>
				<Center style={{ width: 21, height: 21 }}>
					<Icon
						size={16}
						stroke={1.5}
					/>
				</Center>
			</UnstyledButton>
		</Table.Th>
	);
}

interface LicenseTableProps {
	licenses: License[];
	currentUserRole?: string;
	selectedLicenses: string[];
	sortField?: LicenseSortField;
	sortDirection?: SortDirection;
	onSort?: (field: LicenseSortField) => void;
	onSelectionChange: (selected: string[]) => void;
	onEditLicense: (license: License) => void;
	onToggleLicense: (license: License) => void;
	onDeleteLicense: (id: string, name: string) => void;
	onRevokeLicense: (license: License) => void;
	onReuseLicense: (license: License) => void;
	onRenewLicense: (license: License) => void;
	onHoldLicense: (license: License, action: "hold" | "release") => void;
}

// Appended once at module load. Building the element during render appended a
// fresh copy on every pass, because `contains` is always false for a new node.
const ANIMATION_STYLE_ID = "license-table-animations";
if (!document.getElementById(ANIMATION_STYLE_ID)) {
	const style = document.createElement("style");
	style.id = ANIMATION_STYLE_ID;
	style.textContent = `
		@keyframes pulse {
			0%, 100% { opacity: 1; }
			50% { opacity: 0.8; }
		}
		@keyframes holdGlow {
			0%, 100% { box-shadow: 0 0 0 0 rgba(250, 176, 5, 0.3); }
			50% { box-shadow: 0 0 0 2px rgba(250, 176, 5, 0.1); }
		}
		.hold-badge {
			animation: pulse 2s infinite;
		}
		.hold-row {
			animation: holdGlow 3s infinite;
		}
	`;
	document.head.appendChild(style);
}

// Helper functions for license state
const getLicenseState = (license: License) => {
	return {
		isRevoked: !license.isActive && license.status === "revoked",
		isInitialized: !license.isActive && license.status === "initialized",
		isOnHold: license.status === "hold",
		isExpired: license.isExpired,
		isActive: license.isActive,
	};
};

// Deactivate only. A license becomes active when a station activates it, and
// a held one comes back through Release - the backend refuses both here.
const canToggleLicense = (license: License) => {
	const { isActive, isExpired } = getLicenseState(license);
	return isActive && !isExpired;
};

const canEditLicense = (license: License) => {
	const { isRevoked } = getLicenseState(license);
	// Allow editing even if expired
	return !isRevoked;
};

const canRenewLicense = (license: License) => {
	const { isRevoked, isExpired } = getLicenseState(license);
	// Allow renewing expired licenses, but not revoked ones
	return !isRevoked && isExpired;
};

const canHoldLicense = (license: License) => {
	const { isRevoked, isExpired } = getLicenseState(license);
	const isActiveOrOnHold =
		license.status === "active" || license.status === "hold";
	// Cannot hold expired licenses
	return !isRevoked && !isExpired && isActiveOrOnHold;
};

const canRevokeLicense = (license: License) => {
	const { isRevoked, isActive, isExpired } = getLicenseState(license);
	// Cannot revoke expired licenses
	return !isRevoked && isActive && !isExpired;
};

const canReuseLicense = (license: License) => {
	const { isRevoked, isExpired } = getLicenseState(license);
	// Re-use resets a revoked license to initialized so it can be handed to a
	// new station. Pointless on an expired one - it still cannot activate.
	return isRevoked && !isExpired;
};

const getDaysRemaining = (expirationDate: string) => {
	const expDate = new Date(expirationDate);
	const today = new Date();
	const diffTime = expDate.getTime() - today.getTime();
	const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
	return diffDays;
};

const NO_VALUE = "-";

function LicenseTable({
	licenses,
	currentUserRole,
	selectedLicenses,
	sortField,
	sortDirection,
	onSort,
	onSelectionChange,
	onEditLicense,
	onToggleLicense,
	onDeleteLicense,
	onRevokeLicense,
	onReuseLicense,
	onRenewLicense,
	onHoldLicense,
}: LicenseTableProps) {
	const canModifyLicenses =
		currentUserRole === "admin" || currentUserRole === "manager";

	const handleSort = (field: LicenseSortField) => {
		if (onSort) {
			onSort(field);
		}
	};

	const handleSelectAll = (checked: boolean) => {
		if (checked) {
			onSelectionChange(licenses.map((l) => l._id));
		} else {
			onSelectionChange([]);
		}
	};

	const handleSelectOne = (licenseId: string, checked: boolean) => {
		if (checked) {
			onSelectionChange([...selectedLicenses, licenseId]);
		} else {
			onSelectionChange(selectedLicenses.filter((id) => id !== licenseId));
		}
	};

	// Against this page's rows: the selection can also hold every license
	// matching the filters, most of them on other pages
	const allSelected =
		licenses.length > 0 &&
		licenses.every((license) => selectedLicenses.includes(license._id));
	const someSelected =
		!allSelected &&
		licenses.some((license) => selectedLicenses.includes(license._id));

	const getDaysRemainingBadge = (license: License) => {
		if (license.isUnlimited) {
			return (
				<Badge
					color="blue"
					variant="light"
					size="sm"
					leftSection={<IconInfinity size={14} />}>
					Unlimited
				</Badge>
			);
		}

		const { isExpired } = getLicenseState(license);

		if (isExpired) {
			return (
				<Badge
					color="red"
					variant="light"
					size="sm">
					Expired
				</Badge>
			);
		}

		// Predates the unlimited-licence option, so it has no expiry to count to
		if (!license.expirationDate) {
			return (
				<Text
					size="sm"
					c="dimmed">
					{NO_VALUE}
				</Text>
			);
		}

		const daysLeft = getDaysRemaining(license.expirationDate);

		if (daysLeft <= 7) {
			return (
				<Badge
					color="red"
					variant="light"
					size="lg">
					{daysLeft}
				</Badge>
			);
		} else if (daysLeft <= 30) {
			return (
				<Badge
					color="orange"
					variant="light"
					size="lg">
					{daysLeft}
				</Badge>
			);
		} else {
			return <Text size="sm">{daysLeft}</Text>;
		}
	};

	// Rendered from the server's resolved state, which is also what the status
	// filter and the overview cards count - so a filter cannot return rows that
	// badge as something else.
	const getStatusBadge = (license: License) => {
		if (license.displayStatus === "revoked") {
			return (
				<Badge
					color="red"
					variant="light">
					Revoked
				</Badge>
			);
		} else if (license.displayStatus === "expired") {
			return (
				<Badge
					color="red"
					variant="light">
					Expired
				</Badge>
			);
		} else if (license.displayStatus === "hold") {
			return (
				<Badge
					color="yellow"
					variant="filled"
					size="lg"
					leftSection={<IconClock size={14} />}
					className="hold-badge"
					style={{
						background: "linear-gradient(45deg, #ffd43b, #fab005)",
						color: "#000",
						fontWeight: 600,
						boxShadow: "0 2px 4px rgba(250, 176, 5, 0.3)",
						textShadow: "0 1px 2px rgba(0, 0, 0, 0.1)",
					}}>
					ON HOLD
				</Badge>
			);
		} else if (license.displayStatus === "initialized") {
			return (
				<Badge
					color="orange"
					variant="light">
					Initialized
				</Badge>
			);
		} else if (license.displayStatus === "inactive") {
			return (
				<Badge
					color="gray"
					variant="light">
					Inactive
				</Badge>
			);
		} else {
			return (
				<Badge
					color="green"
					variant="light">
					Active
				</Badge>
			);
		}
	};

	return (
		<Table.ScrollContainer
			minWidth={500}
			maxHeight={600}>
			<Table
				striped
				highlightOnHover>
				<Table.Thead>
					<Table.Tr>
						{canModifyLicenses && (
							<Table.Th style={{ width: 40 }}>
								<Checkbox
									checked={allSelected}
									indeterminate={someSelected}
									onChange={(e) => handleSelectAll(e.currentTarget.checked)}
									aria-label="Select all licenses"
								/>
							</Table.Th>
						)}
						<ThSortable
							sorted={sortField === "name"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("name")}>
							Name
						</ThSortable>
						<ThSortable
							sorted={sortField === "uid"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("uid")}>
							UID
						</ThSortable>
						<ThSortable
							sorted={sortField === "clientUid"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("clientUid")}>
							Client UID
						</ThSortable>
						<ThSortable
							sorted={sortField === "activationDate"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("activationDate")}>
							Activated
						</ThSortable>
						<ThSortable
							sorted={sortField === "account"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("account")}>
							Account
						</ThSortable>
						<Table.Th>Type</Table.Th>
						<ThSortable
							sorted={sortField === "os"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("os")}>
							OS
						</ThSortable>
						<ThSortable
							sorted={sortField === "status"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("status")}>
							Status
						</ThSortable>
						<ThSortable
							sorted={sortField === "expirationDate"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("expirationDate")}>
							Expiration
						</ThSortable>
						<ThSortable
							sorted={sortField === "daysLeft"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("daysLeft")}>
							Days Left
						</ThSortable>
						<ThSortable
							sorted={sortField === "daysSinceHold"}
							reversed={sortDirection === "asc"}
							onSort={() => handleSort("daysSinceHold")}>
							Days on Hold
						</ThSortable>
						{/* <Table.Th>License Token</Table.Th> */}
						<Table.Th>Actions</Table.Th>
					</Table.Tr>
				</Table.Thead>

				<Table.Tbody>
					{licenses.map((license) => (
						<Table.Tr
							key={license._id || license.uid}
							className={license.displayStatus === "hold" ? "hold-row" : ""}
							style={{
								backgroundColor: selectedLicenses.includes(license._id)
									? "rgba(34, 139, 230, 0.08)"
									: license.displayStatus === "hold"
										? "rgba(255, 212, 59, 0.08)"
										: undefined,
								borderLeft: selectedLicenses.includes(license._id)
									? "4px solid #228be6"
									: license.displayStatus === "hold"
										? "4px solid #fab005"
										: undefined,
								position: "relative",
								...(license.displayStatus === "hold" && {
									background:
										"repeating-linear-gradient(45deg, rgba(255, 212, 59, 0.05), rgba(255, 212, 59, 0.05) 10px, rgba(250, 176, 5, 0.1) 10px, rgba(250, 176, 5, 0.1) 20px)",
								}),
							}}>
							{canModifyLicenses && (
								<Table.Td>
									<Checkbox
										checked={selectedLicenses.includes(license._id)}
										onChange={(e) =>
											handleSelectOne(license._id, e.currentTarget.checked)
										}
										aria-label={`Select ${license.name}`}
									/>
								</Table.Td>
							)}
							<Table.Td>
								<div>
									<Group
										gap="xs"
										align="center">
										{license.displayStatus === "hold" && (
											<Tooltip
												label="This license is currently on hold"
												withArrow>
												<IconClock
													size={16}
													color="#fab005"
													style={{
														filter:
															"drop-shadow(0 1px 2px rgba(250, 176, 5, 0.3))",
													}}
												/>
											</Tooltip>
										)}
										<Text
											fw={500}
											style={{
												color:
													license.displayStatus === "hold"
														? "#e67700"
														: undefined,
												fontWeight:
													license.displayStatus === "hold" ? 600 : 500,
											}}>
											{license.name}
										</Text>
									</Group>
									{license.deviceInfo?.deviceName && (
										<Text
											size="sm"
											c="dimmed"
											style={{
												color:
													license.displayStatus === "hold"
														? "#fab005"
														: undefined,
												opacity:
													license.displayStatus === "hold" ? 0.8 : undefined,
											}}>
											{license.deviceInfo.deviceName}
										</Text>
									)}
								</div>
							</Table.Td>
							<Table.Td>
								<Group gap="xs">
									<Text
										size="sm"
										c="dimmed">
										{license.uid}
									</Text>
									<CopyButton value={license.uid}>
										{({ copied, copy }) => (
											<Tooltip label={copied ? "Copied" : "Copy UID"}>
												<ActionIcon
													variant="subtle"
													color={copied ? "teal" : "gray"}
													onClick={copy}
													size="sm">
													{copied ? (
														<IconCheck size={12} />
													) : (
														<IconCopy size={12} />
													)}
												</ActionIcon>
											</Tooltip>
										)}
									</CopyButton>
								</Group>
							</Table.Td>
							<Table.Td>
								<Text
									size="sm"
									c="dimmed">
									{license.clientUid || "-"}
								</Text>
							</Table.Td>
							<Table.Td>
								<Text size="sm">
									{license.activationDate
										? new Date(license.activationDate).toLocaleDateString(
												"he-IL",
											)
										: NO_VALUE}
								</Text>
							</Table.Td>
							<Table.Td>
								<div>
									<Text size="sm">{license.accountId?.name}</Text>
								</div>
							</Table.Td>
							<Table.Td>
								<Text
									size="sm"
									c={license.licenseTypeId ? undefined : "dimmed"}>
									{license.licenseTypeId?.name || "Unassigned"}
								</Text>
							</Table.Td>
							<Table.Td>
								<Text size="sm">{license.os}</Text>
							</Table.Td>
							<Table.Td>{getStatusBadge(license)}</Table.Td>
							<Table.Td>
								{license.isUnlimited ? (
									<Badge
										color="blue"
										variant="light"
										size="sm"
										leftSection={<IconInfinity size={14} />}>
										Unlimited
									</Badge>
								) : (
									<Text size="sm">
										{license.expirationDate
											? new Date(license.expirationDate).toLocaleDateString(
													"he-IL",
												)
											: NO_VALUE}
									</Text>
								)}
							</Table.Td>
							<Table.Td>{getDaysRemainingBadge(license)}</Table.Td>
							<Table.Td>
								{license.status === "hold" &&
								license.daysSinceHold !== undefined &&
								license.daysSinceHold !== null ? (
									<Badge
										color="yellow"
										variant="filled"
										size="lg"
										style={{
											background: "linear-gradient(45deg, #ffd43b, #fab005)",
											color: "#000",
											fontWeight: 600,
										}}>
										{license.daysSinceHold}{" "}
										{license.daysSinceHold === 1 ? "day" : "days"}
									</Badge>
								) : (
									<Text
										size="sm"
										c="dimmed">
										-
									</Text>
								)}
							</Table.Td>
							{/* <Table.Td>
								<Group gap="xs">
									<TokenCopyButton
										licenseId={license._id}
										isRevoked={isRevoked(license)}
									/>
								</Group>
							</Table.Td> */}
							<Table.Td>
								{canModifyLicenses && (
									<Menu
										withinPortal
										position="bottom-end">
										<Menu.Target>
											<ActionIcon
												variant="light"
												aria-label="License actions">
												<IconDotsVertical size={16} />
											</ActionIcon>
										</Menu.Target>
										<Menu.Dropdown>
											{canToggleLicense(license) && (
												<Menu.Item
													leftSection={<IconPower size={14} />}
													onClick={() => onToggleLicense(license)}>
													{license.isActive ? "Deactivate" : "Activate"}
												</Menu.Item>
											)}
											{canEditLicense(license) && (
												<Menu.Item
													leftSection={<IconEdit size={14} />}
													onClick={() => onEditLicense(license)}>
													Edit
												</Menu.Item>
											)}
											{canRenewLicense(license) && (
												<Menu.Item
													leftSection={<IconRefresh size={14} />}
													onClick={() => onRenewLicense(license)}>
													Renew
												</Menu.Item>
											)}
											{canHoldLicense(license) && (
												<Menu.Item
													leftSection={
														license.status === "hold" ? (
															<IconPlayerPlay size={14} />
														) : (
															<IconPlayerPause size={14} />
														)
													}
													onClick={() =>
														onHoldLicense(
															license,
															license.status === "hold" ? "release" : "hold",
														)
													}>
													{license.status === "hold"
														? "Release from Hold"
														: "Put on Hold"}
												</Menu.Item>
											)}
											{canRevokeLicense(license) && (
												<Menu.Item
													leftSection={<IconX size={14} />}
													color="orange"
													onClick={() => onRevokeLicense(license)}>
													Revoke
												</Menu.Item>
											)}
											{canReuseLicense(license) && (
												<Menu.Item
													leftSection={<IconRecycle size={14} />}
													onClick={() => onReuseLicense(license)}>
													Re-use
												</Menu.Item>
											)}
											<Menu.Divider />
											<Menu.Item
												leftSection={<IconTrash size={14} />}
												color="red"
												onClick={() =>
													onDeleteLicense(license._id, license.name)
												}>
												Delete
											</Menu.Item>
										</Menu.Dropdown>
									</Menu>
								)}
							</Table.Td>
						</Table.Tr>
					))}
				</Table.Tbody>
			</Table>
		</Table.ScrollContainer>
	);
}

export default LicenseTable;
